import {TextureType, Gamma, Hsv, isVec2, RgbCurve} from "@cm/material-nodes"
import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import type {Format, ImageMapInfo, InputMapsInfo, InputMapsNodes, NormalY} from "#material-maps-exporter/core/types"
import {
    IMG_FORMAT_TO_ENCODER_FN,
    isSupportedImageTransformNode,
    materialMapsExporterLogger,
    PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT,
} from "#material-maps-exporter/core/types"

export function imageProcessingGraphForInputMaps(mapsInfo: InputMapsInfo): InputMapsNodes {
    const mapsNodes: InputMapsNodes = {}

    // TODO: move into backend
    for (const type of Object.keys(mapsInfo)) {
        const mapInfo = mapsInfo[type]
        let image = ((): Nodes.ImageNode => {
            if (mapInfo.data.type === "ShaderNodeRGB") {
                const color = mapInfo.data.shaderNodeRGB.parameters.settings.color
                if (!color) throw Error(`ShaderNodeRGB has no color parameter`)
                return Nodes.createImage(1, 1, "float32", "linear", [color.r, color.g, color.b])
            } else {
                const dataObjRef = JobNodes.dataObjectReference(mapInfo.data.dataObjectId)
                const decodedImage = Nodes.decode(Nodes.externalData(dataObjRef, "encodedData"))
                return decodedImage
            }
        })()

        image = mapInfo.transformations.reduce((acc, transformation) => {
            if (!isSupportedImageTransformNode(transformation)) throw Error(`Unsupported image transformation: ${transformation.nodeClass}`)
            if (transformation.parameters.ui?.disabled) return acc

            if (transformation instanceof RgbCurve) {
                if (transformation.parameters === undefined) throw Error(`RGBCurve node has no parameters`)
                const {fac, ...curveParameters} = transformation.parameters.settings
                if (typeof fac !== "number") throw Error(`RGBCurve node has no valid Fac parameter`)

                const toneMappingParameters = Object.fromEntries<[number, number]>(
                    Object.entries(RgbCurve.settingsFromControlPoints(RgbCurve.controlPointsFromSettings(curveParameters))).map(([key, value]) => {
                        if (!isVec2(value)) throw Error(`Invalid curve parameter value for key ${key}: ${value}`)
                        return [key, [value.x, value.y]]
                    }),
                )

                const toneMap: Nodes.ToneMap = {
                    type: "toneMap",
                    input: acc,
                    mode: "rgbCurve",
                    parameters: {fac: fac, ...toneMappingParameters},
                }
                return toneMap
            } else if (transformation instanceof Hsv) {
                if (transformation.parameters === undefined) throw Error(`HueSaturation node has no parameters`)
                const {fac, hue, saturation, value} = transformation.parameters.settings
                if (typeof fac !== "number") throw Error(`HueSaturation node has no valid Fac parameter`)
                if (typeof hue !== "number") throw Error(`HueSaturation node has no valid Hue parameter`)
                if (typeof saturation !== "number") throw Error(`HueSaturation node has no valid Saturation parameter`)
                if (typeof value !== "number") throw Error(`HueSaturation node has no valid Value parameter`)

                const toneMap: Nodes.ToneMap = {
                    type: "toneMap",
                    input: acc,
                    mode: "hueSaturation",
                    parameters: {fac, hue, saturation, value},
                }
                return toneMap
            } else if (transformation instanceof Gamma) {
                if (transformation.parameters === undefined) throw Error(`Gamma node has no parameters`)
                const {gamma} = transformation.parameters.settings
                if (typeof gamma !== "number") throw Error(`Gamma node has no valid Gamma parameter`)

                const mathNode: Nodes.Math = {
                    type: "math",
                    firstInput: acc,
                    secondInput: gamma,
                    operation: "pow",
                }
                return mathNode
            } else throw Error(`Image transformation not yet supported`)
        }, image)

        //All potential further computations on the maps should be done in floating linear color space
        image = Nodes.convert(image, "float32", "RGBA", false)

        const channelLayout = PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT[type]
        // Some of the manually modified maps are not necessarily stored with L/RGB layout.
        // Explicitly convert image data into format with the expected layout to avoid disambiguity
        if (channelLayout === "L") {
            mapsNodes[type] = Nodes.extractChannel(image, 0)
        } else if (channelLayout === "RGB") {
            mapsNodes[type] = Nodes.combineChannels([Nodes.extractChannel(image, 0), Nodes.extractChannel(image, 1), Nodes.extractChannel(image, 2)], "RGB")
        } else {
            throw Error(`Unsupported channel layout for texture type: ${type}`)
        }
    }

    return mapsNodes
}

export function mix(a: Nodes.ImageNode, b: Nodes.ImageNode | number, m: Nodes.ImageNode | number): Nodes.Math {
    const b_ = typeof b === "number" ? Nodes.math("constLike", a, b) : b
    const m_ = typeof m === "number" ? Nodes.math("constLike", a, m) : m
    return Nodes.math("-", Nodes.math("*", b_, m), Nodes.math("*", a, Nodes.math("-", m_, 1)))
}

export function encodeConvertedNode(
    imageNode: Nodes.ImageNode,
    format: Format,
    dpi: number | undefined,
    colorData = false,
    singleChannel = true,
): Nodes.Encode {
    let dataType: Nodes.Convert["dataType"]
    let options: {[key: string]: unknown} = {}
    let forceLinear: boolean

    if (format === "exr") {
        dataType = "float32"
        options = {bitDepth: 16}
        forceLinear = true
    } else if (format === "tiff") {
        dataType = "uint8"
        forceLinear = false
    } else if (format === "png") {
        dataType = "uint8"
        forceLinear = false
    } else if (format === "jpeg") {
        dataType = "uint8"
        forceLinear = false
    } else {
        throw Error(`Unsupported format: ${format}`)
    }

    const sRGB = colorData && !forceLinear
    let output: Nodes.ImageNode
    if (singleChannel) {
        output = Nodes.convert(imageNode, dataType, "L", sRGB)
    } else {
        output = Nodes.combineChannels(
            [
                Nodes.convert(Nodes.extractChannel(imageNode, 0), dataType, "L", sRGB),
                Nodes.convert(Nodes.extractChannel(imageNode, 1), dataType, "L", sRGB),
                Nodes.convert(Nodes.extractChannel(imageNode, 2), dataType, "L", sRGB),
            ],
            "RGB",
        )
    }

    return Nodes.encode(Nodes.setDpi(output, dpi), IMG_FORMAT_TO_ENCODER_FN[format], options)
}

export function provideFallbackMap(
    fallbackResX: number,
    fallbackResY: number,
    colorSpace: "linear" | "sRGB",
    fallbackColor: Nodes.RGBColor | number,
): Nodes.ImageNode {
    return Nodes.createImage(fallbackResX, fallbackResY, "float32", colorSpace, fallbackColor)
}

export function getMapNodeOrProvideFallback(
    mapsNodes: InputMapsNodes,
    fallbackResX: number,
    fallbackResY: number,
    type: string,
    fallbackColor: Nodes.RGBColor | number,
    colorSpace: "linear" | "sRGB",
): Nodes.ImageNode {
    if (mapsNodes[type]) return mapsNodes[type]
    materialMapsExporterLogger?.debug(
        `Generating fallback map for ${type} with resolution ${fallbackResX}x${fallbackResY}, color ${fallbackColor} and ${colorSpace} color space`,
    )

    return provideFallbackMap(fallbackResX, fallbackResY, colorSpace, fallbackColor)
}

export function getImageMapInfos(mapsInfo: InputMapsInfo): ImageMapInfo[] {
    const imageMapsInfo: ImageMapInfo[] = []
    for (const textureType of Object.keys(mapsInfo)) {
        const info = mapsInfo[textureType]
        if (info.data.type === "DataObject") {
            const {width, height, physicalWidth, physicalHeight, displacementScale} = info.data
            imageMapsInfo.push({width, height, physicalWidth, physicalHeight, textureType, displacementScale})
        }
    }
    return imageMapsInfo
}

export function convertNormal(normal: Nodes.ImageNode, option: NormalY, def: Exclude<NormalY, "default">): Nodes.ImageNode {
    const normalY = option === "default" || option === undefined ? def : option

    switch (normalY) {
        case "y+down":
            return normal
        case "y+up": {
            const chX = Nodes.extractChannel(normal, 0)
            const chY = Nodes.math("+", Nodes.math("*", Nodes.extractChannel(normal, 1), -1), 1)
            const chZ = Nodes.extractChannel(normal, 2)
            return Nodes.combineChannels([chX, chY, chZ], "RGB")
        }
        default:
            throw Error(`Unsupported normal Y option: ${option}`)
    }
}

function convertTransmissionToMask(transmission: Nodes.ImageNode, transmissionMin: number, transmissionMax: number): Nodes.ImageNode {
    // transmission rgb -> v (brightness) component of transmission hsv
    const rTransmission = Nodes.extractChannel(transmission, 0)
    const gTransmission = Nodes.extractChannel(transmission, 1)
    const bTransmission = Nodes.extractChannel(transmission, 2)
    const vTransmission = Nodes.math("max", rTransmission, Nodes.math("max", gTransmission, bTransmission))

    // rescale, invert, clamp ([minValue...maxValue] -> [1...0])
    const mask = Nodes.math("/", Nodes.math("-", vTransmission, transmissionMax), transmissionMin - transmissionMax)
    const clampedMask = Nodes.math("max", Nodes.math("min", mask, 1), 0)
    return clampedMask
}

export function alphaFromMaskOrTransmission(mapsNodes: InputMapsNodes, mapsInfo: InputMapsInfo): Nodes.ImageNode | null {
    if (mapsNodes[TextureType.Mask]) return mapsNodes[TextureType.Mask]

    const transmission = mapsNodes[TextureType.Transmission]
    if (!transmission) return null

    const transmissionInfo = mapsInfo[TextureType.Transmission].data.type === "DataObject" ? mapsInfo[TextureType.Transmission].data : undefined
    const transmissionMax = transmissionInfo?.transmissionMax ?? 1
    const transmissionMin = transmissionInfo?.transmissionMin ?? 0
    return convertTransmissionToMask(transmission, transmissionMin, transmissionMax)
}
